import styled from '@emotion/styled';
import { MutableRefObject, useEffect, useState } from 'react';
import { Button } from '~/modules/Elements/Button';
import { typography } from '~/modules/Elements/cssMixins';
import { SongGroup } from '~/routes/SingASong/SongSelection/Hooks/useSongList';

interface Props {
  groupedSongList: SongGroup[];
  onScrollToGroup: (group: SongGroup) => void;
  containerRef: MutableRefObject<HTMLDivElement | null>;
}

export default function SongGroupsNavigation({ groupedSongList, onScrollToGroup, containerRef }: Props) {
  const [activeGroups, setActiveGroups] = useState<number[]>([]);

  useEffect(() => {
    // This is slightly complicated logic needed due to virtualization of the list
    // Intersection observer is used to detect which group header is currently visible.
    // Mutation observer catches changes in the list, e.g. if the group header appears or disappears
    // When it happens it restarts the intersection observer with currently attached groups
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = Number(entry.target.getAttribute('data-virtualized-group')!);

        if (entry.isIntersecting) {
          setActiveGroups((current) => [...current, index]);
        } else {
          setActiveGroups((current) => current.filter((group) => group !== index));
        }
      });
    });

    const mutationObserver = new MutationObserver(() => {
      const element = document.querySelectorAll(`[data-virtualized-group]`);
      observer.disconnect();
      setActiveGroups([]);

      element.forEach((el) => {
        observer.observe(el);
      });
    });

    if (containerRef.current) {
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });

      return () => {
        mutationObserver.disconnect();
        setActiveGroups([]);
        observer.disconnect();
      };
    }
  }, [groupedSongList, containerRef]);

  return (
    <>
      <Container>
        {groupedSongList.map((group, index) => {
          const active = activeGroups.includes(index);
          return (
            <SongsGroupButton
              key={group.name}
              onClick={() => onScrollToGroup(group)}
              data-active={active}
              data-test={`group-navigation-${group.name}`}>
              {group.displayShort ?? group.name}
            </SongsGroupButton>
          );
        })}
      </Container>
    </>
  );
}

const Container = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  top: var(--song-catalog-toolbar-height, 0);
  left: var(--song-sidebar-weight);
  min-height: var(--song-group-navigation-height, 46px);
  padding: 6px 8px;
  width: calc(100% - var(--song-sidebar-weight));
  box-sizing: border-box;
  z-index: 100;
  gap: 5px;
  background: var(--walky-surface-translucent);
  border-bottom: 0.2rem solid var(--walky-orange);
  box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.16);
  backdrop-filter: blur(12px);
  overflow-x: auto;

  @media (max-width: 640px) {
    gap: 6px;
    padding: 6px 8px;
    scrollbar-width: thin;
  }
`;

const SongsGroupButton = styled(Button)`
  border: none;
  cursor: pointer;
  ${typography};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: 36px;
  height: 34px;
  padding: 4px 10px;
  font-size: 15px;
  z-index: 1;
  color: var(--walky-ink);
  border-radius: 0.5rem;
  box-shadow: none;
  flex: 0 0 auto;

  @media (max-width: 640px) {
    min-width: 42px;
    height: 42px;
    padding: 5px 11px;
    font-size: 18px;
  }

  &[data-active='true'] {
    background: var(--walky-orange);
    color: white;
  }
  &[data-active='false'] {
    background: transparent;
    color: var(--walky-ink);
  }
`;
